import { ChevronRight, SlidersHorizontal } from "lucide-react";
import { Link } from "wouter";

import { panelToolbarControlClass } from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { userTaskSourcesHref } from "@/types/userTaskLinks.ts";
import {
  type PanelControlDimension,
  PanelDimensionGroup,
} from "@/views/Settings/panelControls.tsx";

/** The trigger's name, which is also what the glyph's tooltip says. */
const MENU_LABEL = "Task list settings";

/**
 * Everything about the task list that is not a task, behind the toolbar's
 * sliders: how the list is drawn, and the door to where the tasks come from.
 *
 * One menu rather than the view behind its own glyph and the rest behind
 * this one. The view answers the same question the filters do — how do I want
 * to look at this list — and a second glyph for it beside this one had the
 * reader guessing which held what. The filters themselves stay spelled out
 * on the toolbar at every width, as Connectors and Routines spell theirs;
 * the native app keeps them in its one menu, where the header has no row.
 *
 * The sources, the muted senders and the rules are one row leading to one
 * page rather than rows of their own here: they answer a different question
 * from the choices above — not what to look at, but where it all comes from —
 * and each list is as long as the account has made it, which is a page's
 * worth and not a menu's. The chevron says the row leaves the menu, as the
 * app's other rows that do say it.
 */
export const TaskListMenu = ({ view }: { view: PanelControlDimension }) => (
  <DropdownMenu>
    <Tooltip>
      <TooltipTrigger asChild>
        <DropdownMenuTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className={panelToolbarControlClass}
            aria-label={MENU_LABEL}
            data-statsig-name="tasks-skips"
          >
            <SlidersHorizontal />
          </Button>
        </DropdownMenuTrigger>
      </TooltipTrigger>
      <TooltipContent>{MENU_LABEL}</TooltipContent>
    </Tooltip>
    {/* Anchored to the trigger's right edge: the glyph sits at the end of the
        toolbar row, so aligning the other end would run the menu off the page
        rather than back across the list. 16 is the gutter the rest of the app
        holds to. */}
    <DropdownMenuContent align="end" collisionPadding={16} className="w-64">
      <PanelDimensionGroup axis={view} />
      <DropdownMenuSeparator />
      <DropdownMenuItem asChild data-statsig-name="tasks-sources">
        <Link href={userTaskSourcesHref()} className="justify-between">
          <span>Sources and rules</span>
          <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
        </Link>
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);
